/// <reference types="vite-plugin-pwa/vanillajs" />
/**
 * Feeds useOffline: navigator online/offline events, a /api/health ping, and
 * (when asked) the service worker registration with its status messages and
 * update prompt. src/main.tsx calls startOffline once.
 *
 * Kept out of index.ts: 'virtual:pwa-register' only resolves inside the Vite
 * build, and the presenter's unit tests import useOffline from index.ts.
 */
import { registerSW } from 'virtual:pwa-register';
import { toast } from '@/components/ui/toast';
import {
  isSwStatusMessage,
  SKIP_WAITING,
  SW_HAS_URL,
  SW_STATUS_REQUEST,
  type SwHasUrlRequest,
} from '@/sw/protocol';
import { setUpdateHandler, useOffline } from './store';

/** Public, cheap, never cached by the service worker (no route matches it). */
const HEALTH_URL = '/api/health';
const HEALTH_INTERVAL_MS = 30_000;
/** A long-running demo tab rarely navigates, so it asks for a new worker on its own. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60_000;
/** A worker that does not answer the build question within this time counts as "not this build". */
const HAS_URL_TIMEOUT_MS = 2000;

export interface StartOfflineOptions {
  /** Register the service worker (production, or dev with VITE_SW=1, and only from signed-in pages). */
  registerServiceWorker: boolean;
}

export function startOffline({ registerServiceWorker }: StartOfflineOptions): void {
  watchNetwork();
  if (registerServiceWorker) register();
}

let lastReachable = true;

async function ping(): Promise<void> {
  // Offline per the browser: nothing to learn; the 'online' event pings again.
  if (!navigator.onLine) return;
  let reachable = false;
  let failure: unknown = null;
  try {
    const res = await fetch(HEALTH_URL, { cache: 'no-store', credentials: 'omit' });
    reachable = res.ok;
    if (!res.ok) failure = new Error(`${HEALTH_URL} answered ${res.status}`);
  } catch (error) {
    failure = error;
  }
  // The failure is the signal (the badge turns OFFLINE); it is logged once per drop, not on every ping.
  if (!reachable && lastReachable) console.warn('[offline] the server is unreachable:', failure);
  lastReachable = reachable;
  useOffline.getState().dispatch({ type: 'health', reachable });
}

function watchNetwork(): void {
  const { dispatch } = useOffline.getState();
  window.addEventListener('online', () => {
    dispatch({ type: 'navigator', online: true });
    void ping();
  });
  window.addEventListener('offline', () => dispatch({ type: 'navigator', online: false }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void ping();
  });
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void ping();
  }, HEALTH_INTERVAL_MS);
  void ping();
}

function requestStatus(): void {
  navigator.serviceWorker.controller?.postMessage({ type: SW_STATUS_REQUEST });
}

/**
 * Whether `worker` serves the build this page runs: its precache holds the page's entry
 * chunk. Every build hashes the entry anew (it names the lazy chunks), so a match means the
 * same build. No answer within HAS_URL_TIMEOUT_MS counts as a different build.
 */
function workerServesThisBuild(worker: ServiceWorker, entryUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => {
      console.warn('[offline] the service worker did not answer which build it serves');
      resolve(false);
    }, HAS_URL_TIMEOUT_MS);
    channel.port1.onmessage = (event: MessageEvent<{ has?: unknown }>) => {
      window.clearTimeout(timer);
      resolve(event.data?.has === true);
    };
    const request: SwHasUrlRequest = { type: SW_HAS_URL, url: entryUrl };
    worker.postMessage(request, [channel.port2]);
  });
}

function register(): void {
  if (!('serviceWorker' in navigator)) {
    console.warn('[offline] this browser has no service worker support; offline boot is unavailable');
    return;
  }
  const sw = navigator.serviceWorker;
  const { dispatch } = useOffline.getState();
  /** The entry chunk this page booted from (production index.html has one module script). */
  const entryUrl = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src ?? null;
  /** The update prompt fires once per waiting worker (workbox-window can report it twice). */
  let updatePrompted = false;

  sw.addEventListener('message', (event: MessageEvent) => {
    if (isSwStatusMessage(event.data)) dispatch({ type: 'sw-status', status: event.data });
  });
  sw.addEventListener('controllerchange', () => {
    // A takeover ends the waiting update; a later one prompts again.
    updatePrompted = false;
    dispatch({ type: 'controller', controlled: sw.controller !== null });
    requestStatus();
  });
  sw.startMessages();
  dispatch({ type: 'controller', controlled: sw.controller !== null });
  requestStatus();

  let registration: ServiceWorkerRegistration | undefined;
  /** Set by the badge's Reload: this page runs the old code and must reload once the new worker controls it. */
  let reloadOnControl = false;
  /** One build check per takeover (workbox-window can report it twice). */
  let takeoverCheck: Promise<void> | null = null;
  const updateSW = registerSW({
    onNeedRefresh() {
      if (updatePrompted) return;
      updatePrompted = true;
      const waiting = registration?.waiting;
      // A page loaded after the deploy (the reload that is handing over, or a new tab) already
      // runs the waiting worker's build: no prompt, the hand-off completes on its own.
      void (waiting && entryUrl ? workerServesThisBuild(waiting, entryUrl) : Promise.resolve(false)).then(
        (alreadyCurrent) => {
          if (alreadyCurrent) return;
          dispatch({ type: 'update-ready' });
          toast('info', 'Update available, reload');
        },
      );
    },
    // A new worker took control. After the badge's Reload this page reloads. Otherwise another
    // tab's reload (the pagehide hand-off below) activated it: a page that already runs the new
    // build stays, an older one reloads so it does not run old code against the new precache.
    onNeedReload() {
      if (reloadOnControl || !entryUrl || !sw.controller) {
        window.location.reload();
        return;
      }
      if (takeoverCheck) return;
      takeoverCheck = workerServesThisBuild(sw.controller, entryUrl).then((current) => {
        if (current) takeoverCheck = null;
        else window.location.reload();
      });
    },
    onOfflineReady() {
      requestStatus();
    },
    onRegisteredSW(_swUrl, reg) {
      registration = reg;
      if (!reg) return;
      window.setInterval(() => {
        if (!navigator.onLine) return;
        reg
          .update()
          .catch((error: unknown) => console.warn('[offline] service worker update check failed:', error));
      }, UPDATE_CHECK_INTERVAL_MS);
    },
    onRegisterError(error: unknown) {
      console.error('[offline] service worker registration failed:', error);
      toast('error', 'Offline support could not start; see the console.');
    },
  });
  // The badge's Reload: activate the waiting worker, then reload once it controls the page.
  setUpdateHandler(() => {
    reloadOnControl = true;
    void updateSW(true);
  });

  // A plain reload also applies a waiting update (the toast says "reload"): the
  // waiting worker activates as this page unloads. Not when the page enters the
  // back/forward cache, which would hand old code to a newer worker.
  window.addEventListener('pagehide', (event) => {
    if (!event.persisted && useOffline.getState().updateReady) {
      registration?.waiting?.postMessage({ type: SKIP_WAITING });
    }
  });
}
